// DocumentPartyDetails.jsx

import React, { useCallback } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS — shared with PO general info until classes are renamed
import 'nodetypes/purchaseorder/view/PurchaseOrderGeneralInfo.css';

// Icons
import Icon from 'svg/Icon';

/**
 * DocumentPartyDetails
 *
 * Muted address / phone / email under a related party, with hover edit
 * affordance that opens the related node in Peek.
 */
const DocumentPartyDetails = React.memo(({
  relatedNode = null,
  address = '',
  phone = '',
  email = '',
  editLabel = 'Edit details',
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { openNode } = useNodeOpenIntent();

  const hasDetails = Boolean(address || phone || email);
  const canOpen = Boolean(relatedNode?.key);

  // ----- HANDLERS ----- //

  const handleOpen = useCallback(() => {
    if (!canOpen) return;
    openNode(relatedNode);
  }, [canOpen, openNode, relatedNode]);

  if (!hasDetails) return null;

  if (!canOpen) {
    return (
      <div className="po-general-info-party-details">
        {address ? <div className="po-general-info-muted">{address}</div> : null}
        {phone ? <div className="po-general-info-muted">{phone}</div> : null}
        {email ? <div className="po-general-info-muted">{email}</div> : null}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="po-general-info-party-details is-editable"
      title={editLabel}
      onClick={handleOpen}
    >
      <div className="po-general-info-party-details-text">
        {address ? <div className="po-general-info-muted">{address}</div> : null}
        {phone ? <div className="po-general-info-muted">{phone}</div> : null}
        {email ? <div className="po-general-info-muted">{email}</div> : null}
      </div>
      <span className="po-general-info-party-details-edit" aria-hidden="true">
        <Icon
          iconKey="edit"
          width={16}
          height={16}
          color={theme.foregroundMuted}
        />
      </span>
    </button>
  );
});

DocumentPartyDetails.displayName = 'DocumentPartyDetails';

export default DocumentPartyDetails;
